import { useEffect, useState } from 'react';
import { api } from '../lib/api';
import { fmtDateTime, fmtDuration } from '../lib/format';
import { PhotoLightbox } from './PhotoLightbox';

interface Photo {
  id: string;
  phase: 'start' | 'end';
  seq: number;
  url: string;
  thumbnailUrl: string;
  width: number | null;
  height: number | null;
  fileSize: number;
  uploadedAt: string;
}

interface DutyLog {
  id: string;
  locationName: string;
  latitude: number;
  longitude: number;
  startAt: string;
  endAt: string | null;
  durationMinutes: number | null;
  note: string | null;
  status: 'in_progress' | 'completed' | 'cancelled';
  dutyType: { name: string };
  officer?: {
    firstName: string;
    lastName: string;
    opdCardNo: string | null;
    phone: string | null;
    unit?: { name: string } | null;
  };
  photos: Photo[];
}

interface Props {
  dutyLogId: string | null;
  onClose: () => void;
  onChanged?: () => void;
}

const STATUS_LABEL: Record<DutyLog['status'], string> = {
  in_progress: 'กำลังดำเนินการ',
  completed: 'เสร็จสิ้น',
  cancelled: 'ยกเลิก',
};

export function DutyLogDetailDialog({ dutyLogId, onClose, onChanged }: Props) {
  const [log, setLog] = useState<DutyLog | null>(null);
  const [loading, setLoading] = useState(false);
  const [ending, setEnding] = useState(false);
  const [lightbox, setLightbox] = useState<{ photos: Photo[]; index: number } | null>(null);

  useEffect(() => {
    if (!dutyLogId) {
      setLog(null);
      return;
    }
    let cancelled = false;
    (async () => {
      setLoading(true);
      try {
        const { data } = await api.get<DutyLog>(`/app/sv/duty-logs/${dutyLogId}`);
        if (!cancelled) setLog(data);
      } catch (err) {
        if (!cancelled) {
          alert(err instanceof Error ? err.message : 'โหลดข้อมูลไม่สำเร็จ');
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [dutyLogId]);

  useEffect(() => {
    if (!dutyLogId) return;
    const prev = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => {
      document.body.style.overflow = prev;
    };
  }, [dutyLogId]);

  if (!dutyLogId) return null;

  async function handleEnd() {
    if (!log) return;
    if (!confirm('ปิดงานนี้แทนเจ้าหน้าที่?')) return;
    setEnding(true);
    try {
      await api.patch(`/app/sv/duty-logs/${log.id}/end`, {});
      const { data } = await api.get<DutyLog>(`/app/sv/duty-logs/${log.id}`);
      setLog(data);
      onChanged?.();
    } catch (err) {
      alert(err instanceof Error ? err.message : 'ปิดงานไม่สำเร็จ');
    } finally {
      setEnding(false);
    }
  }

  const start = log?.photos.filter((p) => p.phase === 'start') ?? [];
  const end = log?.photos.filter((p) => p.phase === 'end') ?? [];

  return (
    <>
      <div
        className="fixed inset-0 z-40 flex items-stretch justify-center overflow-y-auto bg-black/50"
        onClick={onClose}
      >
        <div
          className="relative flex w-full max-w-md flex-col bg-white shadow-xl"
          onClick={(e) => e.stopPropagation()}
        >
          <button
            type="button"
            onClick={onClose}
            className="absolute right-2 top-2 z-10 flex h-10 w-10 items-center justify-center rounded-full text-2xl text-gray-400 active:bg-gray-100"
            aria-label="ปิด"
          >
            ×
          </button>

          {loading || !log ? (
            <div className="p-10 text-center text-sm text-gray-500">กำลังโหลด...</div>
          ) : (
            <>
              <div className="border-b border-gray-100 px-4 py-4 pr-12">
                <p className="text-xs text-gray-500">{log.dutyType.name}</p>
                <h2 className="mt-1 text-lg font-bold leading-snug">{log.locationName}</h2>
                <div className="mt-2">
                  <span className={`badge-${log.status}`}>{STATUS_LABEL[log.status]}</span>
                </div>
              </div>

              <div className="space-y-5 px-4 py-4">
                <Section title="ข้อมูลเจ้าหน้าที่">
                  <Grid>
                    <Field
                      label="เจ้าหน้าที่"
                      value={
                        log.officer ? `${log.officer.firstName} ${log.officer.lastName}` : '-'
                      }
                    />
                    <Field label="เลขบัตร อปพร." value={log.officer?.opdCardNo} />
                    <Field label="สังกัด" value={log.officer?.unit?.name} />
                    <Field label="เบอร์โทร" value={log.officer?.phone} />
                  </Grid>
                </Section>

                <Section title="เวลาปฏิบัติงาน">
                  <Grid>
                    <Field label="เริ่มงาน" value={fmtDateTime(log.startAt)} />
                    <Field label="เลิกงาน" value={fmtDateTime(log.endAt)} />
                    <Field label="ระยะเวลา" value={fmtDuration(log.durationMinutes)} />
                  </Grid>
                </Section>

                <Section title="พิกัดและสถานที่">
                  <Grid>
                    <Field label="ละติจูด" value={log.latitude.toFixed(8)} mono />
                    <Field label="ลองจิจูด" value={log.longitude.toFixed(8)} mono />
                  </Grid>
                  <a
                    href={`https://www.google.com/maps?q=${log.latitude},${log.longitude}`}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="mt-3 inline-flex items-center gap-1 text-sm text-line"
                  >
                    🗺️ เปิดใน Google Maps ↗
                  </a>
                </Section>

                {log.note && (
                  <Section title="หมายเหตุ">
                    <div className="max-h-32 overflow-y-auto whitespace-pre-wrap rounded-xl bg-gray-50 px-3 py-2 text-sm text-gray-800">
                      {log.note}
                    </div>
                  </Section>
                )}

                {start.length > 0 && (
                  <Section title={`รูปยืนยันการเริ่มงาน (${start.length})`}>
                    <PhotoGrid
                      photos={start}
                      onClick={(i) => setLightbox({ photos: start, index: i })}
                    />
                  </Section>
                )}
                {end.length > 0 && (
                  <Section title={`รูปยืนยันการจบงาน (${end.length})`}>
                    <PhotoGrid
                      photos={end}
                      onClick={(i) => setLightbox({ photos: end, index: i })}
                    />
                  </Section>
                )}
              </div>

              <div className="sticky bottom-0 flex flex-wrap items-center justify-between gap-2 border-t border-gray-100 bg-white px-4 py-3">
                {log.status === 'in_progress' ? (
                  <button
                    type="button"
                    onClick={handleEnd}
                    disabled={ending}
                    className="rounded-xl bg-emerald-600 px-4 py-2 text-sm font-medium text-white shadow-sm active:bg-emerald-700 disabled:opacity-50"
                  >
                    {ending ? 'กำลังปิดงาน...' : 'ปิดงาน'}
                  </button>
                ) : (
                  <span />
                )}
                <button
                  type="button"
                  onClick={onClose}
                  className="rounded-xl border border-gray-200 px-4 py-2 text-sm font-medium text-gray-700 active:bg-gray-50"
                >
                  ปิด
                </button>
              </div>
            </>
          )}
        </div>
      </div>

      {lightbox && (
        <PhotoLightbox
          photos={lightbox.photos.map((p) => ({ id: p.id, url: p.url }))}
          initialIndex={lightbox.index}
          onClose={() => setLightbox(null)}
        />
      )}
    </>
  );
}

function Section({ title, children }: { title: string; children: React.ReactNode }) {
  return (
    <section>
      <h3 className="mb-2 text-xs font-semibold uppercase tracking-wide text-gray-500">
        {title}
      </h3>
      {children}
    </section>
  );
}

function Grid({ children }: { children: React.ReactNode }) {
  return <dl className="grid grid-cols-2 gap-x-3 gap-y-3">{children}</dl>;
}

function Field({
  label,
  value,
  mono,
}: {
  label: string;
  value: string | null | undefined;
  mono?: boolean;
}) {
  return (
    <div>
      <dt className="text-xs text-gray-500">{label}</dt>
      <dd className={`text-sm font-medium text-gray-900 ${mono ? 'font-mono text-xs' : ''}`}>
        {value || '-'}
      </dd>
    </div>
  );
}

function PhotoGrid({
  photos,
  onClick,
}: {
  photos: Photo[];
  onClick: (index: number) => void;
}) {
  return (
    <div className="grid grid-cols-3 gap-2">
      {photos.map((p, i) => (
        <button
          key={p.id}
          type="button"
          onClick={() => onClick(i)}
          className="group relative aspect-square overflow-hidden rounded-xl bg-gray-100 ring-1 ring-gray-200 active:ring-2 active:ring-line"
        >
          <img
            src={p.thumbnailUrl}
            alt=""
            className="h-full w-full object-cover"
            loading="lazy"
          />
        </button>
      ))}
    </div>
  );
}
